import type { GeoPoint, LocationMeasured } from '../realtime/contracts';

function formatN0(value: number, language: string): string {
  return new Intl.NumberFormat(language || 'en', { maximumFractionDigits: 0 }).format(value);
}

export function formatHeight(height: number | null | undefined): string | undefined {
  return height != null ? `${height} cm` : undefined;
}

export function formatWeight(weight: number | null | undefined): string | undefined {
  return weight != null ? `${weight} kg` : undefined;
}

export function formatAppleEedHeight(height: number | null | undefined): string | undefined {
  return height != null ? `${Math.floor(height * 100)} cm` : undefined;
}

export function formatAppleEedWeight(weight: number | null | undefined): string | undefined {
  return weight != null ? `${Math.floor(weight)} kg` : undefined;
}

export function formatAsListOfStrings(strings: readonly string[] | null | undefined): string | undefined {
  return strings != null ? strings.join(', ') : undefined;
}

export function formatAccuracy(accuracy: number, language: string): string {
  return `${formatN0(accuracy, language)} m`;
}

export function formatDistanceInKilometers(
  distanceInMeters: number | null | undefined,
  language: string,
): string | undefined {
  if (distanceInMeters == null) {
    return undefined;
  }

  const distanceInKilometers = distanceInMeters / 1000;
  return `${new Intl.NumberFormat(language || 'en', {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  }).format(distanceInKilometers)} km`;
}

export function formatAgencyNameWithDistance(
  displayName: string,
  distanceInMeters: number | null | undefined,
  language: string,
): string {
  const distance = formatDistanceInKilometers(distanceInMeters, language);
  return distance === undefined ? displayName : `${displayName} (${distance})`;
}

export function formatPortalAltitude(position: LocationMeasured, language: string): string | undefined {
  if (position.altitude === null) {
    return undefined;
  }

  let text = `${formatN0(position.altitude, language)} m`;
  if (position.altitudeAccuracy !== null) {
    text += ` ± ${formatN0(position.altitudeAccuracy, language)} m`;
  }

  return text;
}

function formatDecimalInvariant(value: number): string {
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 5,
    maximumFractionDigits: 5,
  }).format(value);
}

export function formatCoordinates(geoPoint: GeoPoint | null | undefined): string | undefined {
  if (geoPoint == null) {
    return undefined;
  }

  const latitudeChar = geoPoint.latitude < 0 ? 'S' : 'N';
  const longitudeChar = geoPoint.longitude < 0 ? 'W' : 'E';
  return `${formatDecimalInvariant(geoPoint.latitude)}${latitudeChar} ${formatDecimalInvariant(geoPoint.longitude)}${longitudeChar}`;
}

function angleToDegree(degrees: number, directionChar: string): string {
  const minutes = (degrees % 1) * 60;
  const seconds = (minutes % 1) * 60;
  return `${Math.floor(degrees)}° ${Math.floor(minutes)}' ${Math.floor(seconds)}"${directionChar}`;
}

export function formatCoordinatesToDegree(geoPoint: GeoPoint | null | undefined): string | undefined {
  if (geoPoint == null) {
    return undefined;
  }

  const latitude = angleToDegree(geoPoint.latitude, geoPoint.latitude < 0 ? 'S' : 'N');
  const longitude = angleToDegree(geoPoint.longitude, geoPoint.longitude < 0 ? 'W' : 'E');
  return `${latitude} ${longitude}`;
}
